import type { QueryClient, QueryFunctionContext } from "@tanstack/react-query";
import { apiRequest } from "./queryClient";
import { authUserSchema, type AuthUser } from "./auth-user";

export function logoutReconciliationInterval(startedAt: number | null, now = Date.now()): number | false {
  // Reconcile delayed destruction for one minute, then leave the warning and
  // explicit retry/focus checks in place instead of polling indefinitely.
  return startedAt !== null && now - startedAt < 60_000 ? 2_000 : false;
}

async function requestLogout(): Promise<void> {
  // Match the app's other bounded requests; older shop-floor browsers may not
  // implement the newer AbortSignal.timeout static method.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await apiRequest("POST", "/api/auth/logout", undefined, {
      signal: controller.signal,
    });
    const confirmation: unknown = await response.json();
    if (!confirmation || typeof confirmation !== "object" ||
        !("message" in confirmation) || confirmation.message !== "Logged out") {
      throw new Error("Logout was not confirmed");
    }
  } finally {
    clearTimeout(timeout);
  }
}

export async function readLogoutSession(signal?: AbortSignal): Promise<{ ended: boolean; user: AuthUser | null }> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch("/api/auth/me", {
      credentials: "include", cache: "no-store", signal: controller.signal,
    });
    if (response.status === 401) return { ended: true, user: null };
    if (response.status === 200) {
      const user = authUserSchema.safeParse(await response.json());
      return { ended: false, user: user.success ? user.data : null };
    }
    return { ended: false, user: null };
  } catch {
    return { ended: false, user: null };
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export async function sessionHasEnded(signal?: AbortSignal): Promise<boolean> {
  // A replacement identity is signed in; only 401 confirms this browser is signed out.
  return (await readLogoutSession(signal)).ended;
}

export async function clearUserQueries(client: QueryClient): Promise<void> {
  const queries = { predicate: (query: { queryKey: readonly unknown[] }) =>
    !(query.queryKey.length === 1 && query.queryKey[0] === "/api/auth/me") };
  await client.cancelQueries(queries);
  // Queries belong to the departing user. The mutation cache owns unsent,
  // persisted shop-floor work and must survive this operation.
  client.removeQueries(queries);
}

/** Clear the departing identity before publishing any confirmed session change. */
export async function readIdentitySession(client: QueryClient, context: QueryFunctionContext): Promise<AuthUser | null> {
  const response = await fetch("/api/auth/me", { credentials: "include", cache: "no-store", signal: context.signal });
  let next: AuthUser | null;
  if (response.status === 401) next = null;
  else {
    if (!response.ok) throw new Error(`${response.status}: ${(await response.text()) || response.statusText}`);
    const identity = authUserSchema.safeParse(await response.json());
    if (!identity.success) throw new Error("Session identity response was incomplete");
    next = identity.data;
  }
  context.signal.throwIfAborted();
  const previous = client.getQueryData<AuthUser | null>(["/api/auth/me"]);
  if (next?.id !== previous?.id) await clearUserQueries(client);
  return next;
}

/** An explicit identity recovery must not reuse a stale or already-running read. */
export async function recheckIdentity(client: QueryClient): Promise<AuthUser | null> {
  await client.cancelQueries({ queryKey: ["/api/auth/me"], exact: true });
  // staleTime 0 alone still serves the cache while the clock reads earlier than the cached
  // identity (e.g. a device clock moved backwards). Invalidate so recovery always asks the server.
  await client.invalidateQueries({ queryKey: ["/api/auth/me"], exact: true, refetchType: "none" });
  return client.fetchQuery({
    queryKey: ["/api/auth/me"],
    staleTime: 0,
    retry: false,
    queryFn: async context => {
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (context.signal.aborted) abort();
      else context.signal.addEventListener("abort", abort, { once: true });
      const deadline = setTimeout(abort, 5_000);
      try {
        return await readIdentitySession(client, { ...context, signal: controller.signal });
      } finally {
        clearTimeout(deadline);
        context.signal.removeEventListener("abort", abort);
      }
    },
  });
}

/** HUB-572: local authentication changes only after the server confirms session destruction. */
export async function confirmLogout(client: QueryClient): Promise<void> {
  // Abort the identity read before POST so it cannot publish an old login.
  // Unrelated page reads still belong to this user until sign-out is confirmed;
  // cancelling them now would strand a first-loading page if logout fails.
  await client.cancelQueries({ queryKey: ["/api/auth/me"], exact: true });
  try {
    await requestLogout();
  } catch (error) {
    // The server may have destroyed the session before its reply was lost.
    // Reconcile that outcome, but retain the user and retry warning if unproven.
    if (!await sessionHasEnded()) throw error;
  }
  // A pre-logout refresh may still be carrying the old user. Cancel it before clearing
  // the cache, so its eventual response cannot make the workstation appear signed in again.
  await client.cancelQueries({ queryKey: ["/api/auth/me"], exact: true });
  await clearUserQueries(client);
  client.setQueryData(["/api/auth/me"], null);
}
